import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { Input } from '@/core/Input';
import type { Player } from '@/gameplay/Player';
import { Vehicle, boxOverlapsWorld, runOverActors } from '@/gameplay/Vehicle';
import { advanceVehicle } from '@/gameplay/vehicleMotion';
import { bodies as worldBodies, circleOverlapsBox, type ActorBody, type BodyRegistry } from '@/world/bodies';
import type { VehicleKind } from '@/meshkit/vehicles/cars';
import type { CollisionWorld } from '@/world/collision';
import type { ParkedVehiclePlacement, TrafficRoute } from '@/world/LevelLoader';

export type TrafficVehicleState = 'driving' | 'braking' | 'waiting' | 'blocked';

export interface TrafficConfig {
  readonly routes: readonly TrafficRoute[];
  readonly parkedVehicles: readonly ParkedVehiclePlacement[];
}

export interface TrafficVehicleSnapshot {
  readonly id: string;
  readonly kind: VehicleKind;
  readonly trafficState: TrafficVehicleState;
  readonly routeId?: string;
  readonly routeIndex: number;
  readonly stealable: boolean;
  readonly moving: boolean;
}

interface TrafficActor {
  readonly vehicle: Vehicle;
  readonly route: TrafficRoute | null;
  readonly chunkId: string | null;
  routeIndex: number;
  trafficState: TrafficVehicleState;
  stoppedAtEnd: boolean;
  readonly stealable: boolean;
  stolen: boolean;
}

export const TRAFFIC = {
  waypointRadius: 0.45,
  turnRate: 2.8,
  acceleration: 3.2,
  brake: 6.8,
  /**
   * Vaste marge bovenop de remweg: reactietijd, plus genoeg zicht om ook bij stilstand nog
   * iets vóór de bumper te zien.
   */
  reactionDistance: 2.6,
  /**
   * Bemonstering van de vooruitgeveegde doos. Ruim onder de kleinste prop waar een auto voor
   * moet remmen (een bolder is 0.56 m), dus er valt niets tussen twee monsters door.
   */
  sensorStep: 0.4,
  /** Extra breedte waarmee de sensor voetgangers ziet die half op de rijbaan staan. */
  pedestrianMargin: 0.35,
  minSpeed: 0.04,
} as const;

export interface TrafficChunkHandles {
  readonly chunkId: string;
  readonly vehicles: readonly Vehicle[];
}

export class TrafficController {
  readonly #actors: TrafficActor[] = [];
  readonly #allVehicles: Vehicle[] = [];
  readonly #scene: THREE.Scene | null;
  readonly #bodies: BodyRegistry | null;

  constructor(config: TrafficConfig, scene: THREE.Scene | null = null, bodies: BodyRegistry | null = null) {
    this.#scene = scene;
    this.#bodies = bodies;
    this.addChunkTraffic('initial', config);
  }

  addChunkTraffic(chunkId: string, config: TrafficConfig): TrafficChunkHandles {
    const vehicles: Vehicle[] = [];
    for (const route of config.routes) {
      if (route.points.length < 2) continue;
      const start = route.points[0]!;
      const next = route.points[1]!;
      const vehicle = new Vehicle({
        id: `traffic-${route.id}`,
        kind: route.kind,
        position: start.clone(),
        rotation: Math.atan2(next.x - start.x, next.y - start.y),
      });
      vehicle.state = 'driving';
      vehicles.push(vehicle);
      this.#addActor({
        vehicle,
        route,
        chunkId,
        routeIndex: 1,
        trafficState: 'driving',
        stoppedAtEnd: false,
        stealable: false,
        stolen: false,
      });
    }

    for (const placement of config.parkedVehicles) {
      const vehicle = new Vehicle({
        id: placement.id,
        kind: placement.kind,
        position: placement.position.clone(),
        rotation: placement.rotation,
      });
      vehicles.push(vehicle);
      this.#addActor({
        vehicle,
        route: null,
        chunkId,
        routeIndex: 0,
        trafficState: 'waiting',
        stoppedAtEnd: true,
        stealable: placement.stealable,
        stolen: false,
      });
    }

    return { chunkId, vehicles };
  }

  removeChunkTraffic(chunkId: string): void {
    for (let index = this.#actors.length - 1; index >= 0; index--) {
      const actor = this.#actors[index]!;
      if (actor.chunkId !== chunkId) continue;
      if (actor.stolen || actor.vehicle.occupied) continue;
      this.#actors.splice(index, 1);
      const vehicleIndex = this.#allVehicles.indexOf(actor.vehicle);
      if (vehicleIndex >= 0) this.#allVehicles.splice(vehicleIndex, 1);
      this.#scene?.remove(actor.vehicle.object);
      this.#bodies?.removeVehicle(actor.vehicle);
    }
  }

  get vehicles(): readonly Vehicle[] {
    return this.#allVehicles;
  }

  get activeCount(): number {
    return this.#actors.filter((actor) => actor.route && !actor.stolen).length;
  }

  get parkedCount(): number {
    return this.#actors.filter((actor) => !actor.route && !actor.stolen).length;
  }

  addToScene(scene: THREE.Scene): void {
    for (const vehicle of this.#allVehicles) {
      if (vehicle.object.parent !== scene) scene.add(vehicle.object);
    }
  }

  update(dt: number, input: Input, world: CollisionWorld, player: Player): void {
    for (const actor of this.#actors) {
      if (!actor.route) {
        this.#updateParked(actor, input, player);
        continue;
      }
      if (actor.stolen || actor.vehicle.occupied) continue;
      this.#updateRouteActor(actor, dt, world, player);
    }
  }

  snapshots(): TrafficVehicleSnapshot[] {
    return this.#actors.map((actor) => {
      const snapshot = {
        id: actor.vehicle.id,
        kind: actor.vehicle.kind,
        trafficState: actor.trafficState,
        routeIndex: actor.routeIndex,
        stealable: actor.stealable,
        moving: actor.route !== null && !actor.stoppedAtEnd && !actor.stolen,
      };
      return actor.route ? { ...snapshot, routeId: actor.route.id } : snapshot;
    });
  }

  #addActor(actor: TrafficActor): void {
    this.#actors.push(actor);
    this.#allVehicles.push(actor.vehicle);
    this.#scene?.add(actor.vehicle.object);
    this.#bodies?.addVehicle(actor.vehicle);
  }

  #updateParked(actor: TrafficActor, input: Input, player: Player): void {
    if (!actor.stealable || actor.stolen) return;
    if (!input.wasPressed('e') || !actor.vehicle.canEnter(player)) return;
    actor.vehicle.enterDriver(player);
    actor.stolen = true;
    actor.trafficState = 'driving';
    bus.emit('vehicle:stolen', { id: actor.vehicle.id, kind: actor.vehicle.kind });
  }

  #updateRouteActor(actor: TrafficActor, dt: number, world: CollisionWorld, player: Player): void {
    const route = actor.route;
    if (!route || actor.stoppedAtEnd) return;

    const target = route.points[actor.routeIndex]!;
    const toTarget = target.clone().sub(actor.vehicle.position);
    if (toTarget.length() <= TRAFFIC.waypointRadius) {
      if (!this.#advanceRoute(actor)) return;
    }

    const currentTarget = route.points[actor.routeIndex]!;
    const rotationBefore = actor.vehicle.rotation;
    const desiredRotation = Math.atan2(
      currentTarget.x - actor.vehicle.position.x,
      currentTarget.y - actor.vehicle.position.y,
    );
    actor.vehicle.rotation = rotateToward(actor.vehicle.rotation, desiredRotation, TRAFFIC.turnRate * dt);

    const blocked = this.#hasForwardBlocker(actor, world, player);
    if (blocked) {
      actor.vehicle.speed = moveToward(actor.vehicle.speed, 0, TRAFFIC.brake * dt);
      actor.trafficState = actor.vehicle.speed > TRAFFIC.minSpeed ? 'braking' : 'waiting';
      actor.vehicle.object.position.set(actor.vehicle.position.x, 0, actor.vehicle.position.y);
      actor.vehicle.object.rotation.y = actor.vehicle.rotation;
      return;
    }

    actor.vehicle.speed = moveToward(actor.vehicle.speed, route.speedLimit, TRAFFIC.acceleration * dt);
    actor.trafficState = 'driving';
    const blockers = this.#allVehicles.filter((vehicle) => vehicle !== actor.vehicle && vehicle.state !== 'burned');
    const advance = advanceVehicle(
      actor.vehicle,
      actor.vehicle.speed * dt,
      actor.vehicle.collisionHalfWidth,
      actor.vehicle.collisionHalfLength,
      world,
      blockers,
    );

    if (advance.blocked && !advance.slid) {
      // Frontaal vast. De koers die we net insloegen leidt nergens heen, dus draai hem terug
      // in plaats van ter plekke te blijven ronddraaien — zo bleef een geblokkeerde auto
      // eeuwig hangen zonder ooit te herstellen.
      actor.vehicle.rotation = rotationBefore;
      actor.vehicle.speed = 0;
      actor.trafficState = 'blocked';
      actor.vehicle.object.position.set(actor.vehicle.position.x, 0, actor.vehicle.position.y);
      actor.vehicle.object.rotation.y = actor.vehicle.rotation;
      return;
    }

    actor.vehicle.object.position.set(actor.vehicle.position.x, 0, actor.vehicle.position.y);
    actor.vehicle.object.rotation.y = actor.vehicle.rotation;

    // Een AI-auto rijdt net zo hard als jij. Zonder bestuurder om over te slaan: het
    // verkeer heeft er geen. Hetzelfde register als de remsensor, zodat "waar remt hij voor"
    // en "wie rijdt hij aan" niet uit elkaar kunnen lopen.
    runOverActors(actor.vehicle, actor.vehicle.speed, this.#bodies ?? worldBodies, null);
  }

  /**
   * Volgende waypoint, of het einde van de route.
   *
   * Een lus zette `routeIndex` terug op 0, en dat betekende iets anders dan het leek: de auto
   * draaide op het eindpunt om en reed de héle rijbaan achteruit terug — tegen de baan in de
   * andere richting in. Alle vier de authored routes zijn rechte lijnen van de ene kant van de
   * wijk naar de andere, dus dat gebeurde elke ronde opnieuw.
   *
   * Een route is geen circuit maar een rijstrook. Loopen betekent daarom: opnieuw beginnen bij
   * het begin. De auto wordt teruggezet op het startpunt en kijkt weer de goede kant op. Dat
   * gebeurt aan de rand van de wijk, waar de streamer meestal toch al niet meer laadt.
   */
  #advanceRoute(actor: TrafficActor): boolean {
    const route = actor.route;
    if (!route) return false;
    if (actor.routeIndex < route.points.length - 1) {
      actor.routeIndex++;
      return true;
    }

    if (route.loop) {
      const start = route.points[0]!;
      const next = route.points[1]!;
      actor.vehicle.position.copy(start);
      actor.vehicle.rotation = Math.atan2(next.x - start.x, next.y - start.y);
      actor.vehicle.object.position.set(start.x, 0, start.y);
      actor.vehicle.object.rotation.y = actor.vehicle.rotation;
      actor.routeIndex = 1;
      return true;
    }

    actor.vehicle.speed = 0;
    actor.trafficState = 'waiting';
    actor.stoppedAtEnd = true;
    return false;
  }

  /**
   * Staat er iets binnen de remweg?
   *
   * Dit was een punt op 5.2 m vooruit plus een lijn-raycast ernaartoe. Twee gaten daarin: de
   * raycast bemonstert in stappen van een halve tile (2 m), dus een lantaarnpaal of een krat
   * tussen twee monsters was onzichtbaar, en de probe was een lijn zonder breedte terwijl een
   * bestelbus 2.28 m breed is. De auto remde dus niet, en werd vervolgens door de botsingstoets
   * dood stilgezet.
   *
   * Nu wordt de eigen doos vooruit geveegd over de afstand die hij écht nodig heeft om te
   * stoppen (v² / 2a plus een reactiemarge), en getoetst tegen de wereld, andere voertuigen én
   * alle levende personages. Dat laatste is niet alleen de speler: het verkeer reed eerder
   * dwars door zijn eigen soldaten heen, en boven `RAM.lethalSpeed` is dat dodelijk.
   */
  #hasForwardBlocker(actor: TrafficActor, world: CollisionWorld, player: Player): boolean {
    const vehicle = actor.vehicle;
    const speed = Math.abs(vehicle.speed);
    const stopDistance = TRAFFIC.reactionDistance + (speed * speed) / (2 * TRAFFIC.brake);
    const forward = new THREE.Vector2(Math.sin(vehicle.rotation), Math.cos(vehicle.rotation));
    const blockers = this.#allVehicles.filter((other) => other !== vehicle && other.state !== 'burned');
    const registry = this.#bodies ?? worldBodies;
    const halfWidth = vehicle.collisionHalfWidth;
    const halfLength = vehicle.collisionHalfLength;
    const sensorHalfWidth = halfWidth + TRAFFIC.pedestrianMargin;
    const probe = new THREE.Vector2();

    for (let distance = TRAFFIC.sensorStep; distance <= stopDistance; distance += TRAFFIC.sensorStep) {
      probe.copy(vehicle.position).addScaledVector(forward, distance);

      if (boxOverlapsWorld(probe.x, probe.y, halfWidth, halfLength, world, blockers, vehicle.rotation)) {
        return true;
      }

      if (circleOverlapsBox(player.position, player.radius, probe.x, probe.y, vehicle.rotation, sensorHalfWidth, halfLength)) {
        return true;
      }

      // De speler zit ook in het register, maar in tests niet altijd; daarom staat hij
      // hierboven apart en vullen we hier de rest aan.
      for (const other of registry.actorsInBox(probe.x, probe.y, vehicle.rotation, sensorHalfWidth, halfLength)) {
        if (other !== (player as unknown as ActorBody)) return true;
      }
    }

    return false;
  }
}

function moveToward(value: number, target: number, amount: number): number {
  if (value < target) return Math.min(target, value + amount);
  if (value > target) return Math.max(target, value - amount);
  return value;
}

function rotateToward(current: number, target: number, amount: number): number {
  const delta = Math.atan2(Math.sin(target - current), Math.cos(target - current));
  if (Math.abs(delta) <= amount) return target;
  return current + Math.sign(delta) * amount;
}
